import { Is } from '../types';
import { requestNotificationPermission } from '../utils/helpers';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications, ScheduleOptions } from '@capacitor/local-notifications';

const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

// Toggle key'leri
type NotificationToggle =
  | 'bildirim_isEkleme'
  | 'bildirim_isTamamlama'
  | 'bildirim_komisyonAlinma'
  | 'bildirim_faturaKesildi'
  | 'bildirim_alacakTahsil';

export interface NotificationSettings {
  bildirim_isEkleme: boolean;
  bildirim_isTamamlama: boolean;
  bildirim_komisyonAlinma: boolean;
  bildirim_faturaKesildi: boolean;
  bildirim_alacakTahsil: boolean;
}

const STORAGE_KEY = 'notification_settings';
const DEFAULT_ICON = '/assets/images/logo-192.png';

const defaultSettings: NotificationSettings = {
  bildirim_isEkleme: true,
  bildirim_isTamamlama: true,
  bildirim_komisyonAlinma: true,
  bildirim_faturaKesildi: true,
  bildirim_alacakTahsil: true,
};

let notifIdCounter = Math.floor(Math.random() * 10000);
const nextId = () => ++notifIdCounter;

/**
 * Native (iOS/Android): Capacitor LocalNotifications
 * Web: ServiceWorker → new Notification() fallback
 */
const fireNotification = async (title: string, body: string): Promise<boolean> => {
  // ── Native ──────────────────────────────────────────────────────────────
  if (isNative) {
    try {
      const options: ScheduleOptions = {
        notifications: [{
          id: nextId(),
          title,
          body,
          schedule: { at: new Date(Date.now() + 300) }, // 300ms sonra göster
          sound: undefined,
          smallIcon: 'ic_launcher',
          iconColor: '#1976d2',
        }],
      };
      await LocalNotifications.schedule(options);
      return true;
    } catch (err) {
      console.warn('[Bildirim] LocalNotifications.schedule hatası:', err);
      return false;
    }
  }

  // ── Web ─────────────────────────────────────────────────────────────────
  const uniqueTag = 'atlas-' + Date.now();

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body,
        icon: DEFAULT_ICON,
        badge: DEFAULT_ICON,
        vibrate: [200, 100, 200],
        tag: uniqueTag,
        requireInteraction: true,
        silent: false,
      });
      return true;
    } catch {
      // SW'den sonra fallback'e düş
    }
  }

  try {
    new Notification(title, { body, icon: DEFAULT_ICON, tag: uniqueTag });
    return true;
  } catch {
    return false;
  }
};

export const notificationService = {
  requestPermission: async (): Promise<NotificationPermission> => {
    if (isNative) {
      try {
        const result = await LocalNotifications.requestPermissions();
        return result.display === 'granted' ? 'granted' : 'denied';
      } catch {
        return 'denied';
      }
    }
    if (!('Notification' in window)) return 'denied';
    return requestNotificationPermission();
  },

  showNotification: async (
    title: string,
    body: string,
    toggleKey: NotificationToggle,
    _icon?: string
  ): Promise<boolean> => {
    const settings = notificationService.getNotificationSettings();
    if (!settings[toggleKey]) return false;

    // Native izin kontrolü
    if (isNative) {
      try {
        const perm = await LocalNotifications.checkPermissions();
        if (perm.display !== 'granted') {
          const req = await LocalNotifications.requestPermissions();
          if (req.display !== 'granted') return false;
        }
      } catch {
        return false;
      }
      return fireNotification(title, body);
    }

    // Web izin kontrolü
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'default') {
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') return false;
    }
    if (Notification.permission === 'denied') return false;

    return fireNotification(title, body);
  },

  scheduleNotification: (
    title: string,
    body: string,
    toggleKey: NotificationToggle,
    delayMs: number
  ): ReturnType<typeof setTimeout> => {
    return setTimeout(() => {
      notificationService.showNotification(title, body, toggleKey);
    }, delayMs);
  },

  checkOverdueItems: (jobs: Is[]): void => {
    const today = new Date().toISOString().split('T')[0];
    const COOLDOWN_KEY = `overdue_notif_${today}`;

    // Bugün zaten çalıştırıldıysa tekrar çalıştırma
    if (localStorage.getItem(COOLDOWN_KEY)) return;

    const now = new Date();

    for (const job of jobs) {
      if (!job.tamamlandi || !job.tamamlanmaTarihi) continue;

      const tamamlanma = new Date(job.tamamlanmaTarihi);
      const daysDiff = Math.floor(
        (now.getTime() - tamamlanma.getTime()) / (1000 * 60 * 60 * 24)
      );
      const label = `${(job.nereden || '').toUpperCase()} > ${(job.nereye || '').toUpperCase()} "${(job.firma || '').toUpperCase()}"`;

      if (!job.komisyonAlindi && daysDiff >= 3) {
        notificationService.showNotification(
          'Komisyon Hatırlatma',
          `${label} işinin komisyonu ${daysDiff} gündür alınmadı!`,
          'bildirim_komisyonAlinma'
        );
      }

      if (!job.faturaKesildi && daysDiff >= 7) {
        notificationService.showNotification(
          'Fatura Hatırlatma',
          `${label} işinin faturası ${daysDiff} gündür kesilmedi!`,
          'bildirim_faturaKesildi'
        );
      }

      if (!job.odemeAlindi && daysDiff >= 30) {
        notificationService.showNotification(
          'Alacak Hatırlatma',
          `${label} işinin ödemesi ${daysDiff} gündür alınmadı!`,
          'bildirim_alacakTahsil'
        );
      }
    }

    try {
      localStorage.setItem(COOLDOWN_KEY, '1');
    } catch { /* storage full */ }
  },

  saveNotificationSettings: (settings: NotificationSettings): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  },

  getNotificationSettings: (): NotificationSettings => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaultSettings, ...JSON.parse(stored) };
      }
    } catch {
      // ignore
    }
    return { ...defaultSettings };
  },
};
